/**
 * ElectronBridge
 * Electron 平台的 Bridge 實作
 */

import { BridgeStrategy } from './BridgeStrategy'
import { BridgeAction } from '../types/bridge.types'
import type { PlatformType, WindowWithBridge } from '../types/platform.types'

type ElectronListener = (event: unknown, payload: unknown) => void

interface ElectronIpcRenderer {
  invoke?: (channel: string, data?: unknown) => Promise<unknown>
  send?: (channel: string, data?: unknown) => void
  on?: (channel: string, listener: ElectronListener) => void
  removeListener?: (channel: string, listener: ElectronListener) => void
  removeAllListeners?: (channel: string) => void
}

interface ElectronWindow extends WindowWithBridge {
  electron?: {
    ipcRenderer?: ElectronIpcRenderer
  }
}

export class ElectronBridge extends BridgeStrategy {
  private ipcRenderer: ElectronIpcRenderer | null = null

  getPlatform(): PlatformType {
    return 'electron'
  }

  async connect(): Promise<void> {
    this.updateConnectionStatus('connecting')

    try {
      this.ipcRenderer = this.resolveIpcRenderer()
      this.updateConnectionStatus('connected')
    } catch (error) {
      this.updateConnectionStatus('error')
      throw error
    }
  }

  disconnect(): void {
    this.ipcRenderer = null
    this.updateConnectionStatus('disconnected')
  }

  async sendMessage<T>(action: BridgeAction, data?: unknown): Promise<T> {
    const ipc = this.ensureIpcRenderer()
    const actionName = action as string

    if (typeof ipc.invoke === 'function') {
      try {
        const result = await ipc.invoke(actionName, data)
        this.updateLastActivity()
        return this.parseResponse<T>(result)
      } catch (error) {
        throw error instanceof Error
          ? error
          : new Error(`ElectronBridge invoke failed for ${actionName}`)
      }
    }

    throw new Error('Electron ipcRenderer does not support invoke')
  }

  private resolveIpcRenderer(): ElectronIpcRenderer {
    const electronWindow = this.win as ElectronWindow

    if (electronWindow.electron?.ipcRenderer) {
      return electronWindow.electron.ipcRenderer
    }

    throw new Error('Electron ipcRenderer is not available on window')
  }

  private ensureIpcRenderer(): ElectronIpcRenderer {
    if (!this.ipcRenderer) {
      this.ipcRenderer = this.resolveIpcRenderer()
    }
    return this.ipcRenderer
  }
}
